/**
 * webcodecsBackend.js — decode tier 2: WebCodecs VideoDecoder painting to a
 * <canvas> (PLAYBACK-HANDOFF.md §8 point 2).
 *
 * Ported from mock FE `createWebCodecsBackend()`. Shared interface with
 * wasmBackend.js: `configure({width,height,fps}), feed(annexB,isKey,ptsUs),
 * pause()/resume(), .paused, destroy()`. Exactly one instance lives per
 * card at a time — repeated create/destroy without a clean teardown is a
 * known WASM/WebCodecs-worker exhaustion risk if this rule is broken.
 *
 * Comes up PAUSED (owner spec): feed() keeps queuing decoded frames
 * regardless of pause state, so the chunk pump never stalls — resume() is
 * the only thing that starts consuming the queue, and it is called
 * exclusively by the autostart gate or an explicit ▶ (never here).
 *
 * ⚠ Never exercised on real hardware — validate once on a machine with
 * hardware HEVC before relying on it (handoff §8 notes).
 */

const MAX_QUEUED_FRAMES = 120; // ~5s at 25fps
const STALL_GAP_MS = 250;

/**
 * @param {object} args
 * @param {HTMLCanvasElement} args.canvasEl
 * @param {string|null} args.codecString  from tierSelect's WebCodecs probe
 * @param {object} args.callbacks  onFirstFrame(), onStall(gapMs), onError(message)
 */
export function createWebCodecsBackend({ canvasEl, codecString, callbacks }) {
  const { onFirstFrame, onStall, onError } = callbacks || {};
  const ctx = canvasEl.getContext('2d');
  let decoder = null;
  let queue = []; // {frame, ptsUs}
  let clockStartWall = null;
  let clockStartPts = null;
  let rafId = null;
  let paused = true;
  let destroyed = false;
  let lastRenderTs = null;

  function scheduleTick() { if (rafId == null && !destroyed) rafId = requestAnimationFrame(tick); }
  function tick() {
    rafId = null;
    if (destroyed || paused || queue.length === 0) return;
    if (clockStartWall == null) { clockStartWall = performance.now(); clockStartPts = queue[0].ptsUs; }
    const nowUs = (performance.now() - clockStartWall) * 1000 + clockStartPts;
    let toDraw = null;
    while (queue.length && queue[0].ptsUs <= nowUs) {
      if (toDraw) toDraw.frame.close(); // drop-late: keep only the newest due frame
      toDraw = queue.shift();
    }
    if (toDraw) {
      if (canvasEl.width !== toDraw.frame.displayWidth || canvasEl.height !== toDraw.frame.displayHeight) {
        canvasEl.width = toDraw.frame.displayWidth || canvasEl.width;
        canvasEl.height = toDraw.frame.displayHeight || canvasEl.height;
      }
      try { ctx.drawImage(toDraw.frame, 0, 0, canvasEl.width, canvasEl.height); } catch (_) { /* ignore */ }
      toDraw.frame.close();
      const now = performance.now();
      if (lastRenderTs == null) {
        onFirstFrame && onFirstFrame();
      } else {
        const gap = now - lastRenderTs;
        if (gap > STALL_GAP_MS) onStall && onStall(gap);
      }
      lastRenderTs = now;
    }
    if (queue.length) scheduleTick();
  }
  function createDecoder() {
    return new VideoDecoder({
      output(frame) {
        if (destroyed) { frame.close(); return; }
        queue.push({ frame, ptsUs: frame.timestamp });
        // Bounded window, drop the OLDEST: a clip paused near the start
        // would otherwise accumulate every remaining decoded frame as an
        // unclosed VideoFrame (GC warning, decoder backpressure stall).
        while (queue.length > MAX_QUEUED_FRAMES) {
          const old = queue.shift();
          try { old.frame.close(); } catch (_) { /* ignore */ }
        }
        scheduleTick();
      },
      error(e) {
        onError && onError((e && e.message) || String(e));
      },
    });
  }
  return {
    configure(cfg) {
      decoder = createDecoder();
      decoder.configure({
        codec: codecString || 'hev1.1.6.L93.B0',
        codedWidth: cfg.width || undefined,
        codedHeight: cfg.height || undefined,
        optimizeForLatency: true,
      });
    },
    feed(annexB, isKey, ptsUs) {
      if (destroyed || !decoder || decoder.state === 'closed') return;
      try {
        decoder.decode(new EncodedVideoChunk({ type: isKey ? 'key' : 'delta', timestamp: ptsUs, data: annexB }));
      } catch (e) {
        onError && onError((e && e.message) || String(e));
      }
    },
    pause() { paused = true; },
    resume() {
      paused = false;
      scheduleTick();
    },
    get paused() { return paused; },
    destroy() {
      destroyed = true;
      if (rafId != null) { cancelAnimationFrame(rafId); rafId = null; }
      queue.forEach((q) => { try { q.frame.close(); } catch (_) { /* ignore */ } });
      queue = [];
      if (decoder && decoder.state !== 'closed') { try { decoder.close(); } catch (_) { /* ignore */ } }
      decoder = null;
    },
  };
}
